import { Reservation } from "@/types/table";
import { CustomerData, ClientStats } from "@/types/client";
import { startOfMonth, isAfter } from "date-fns";

export interface CustomerNotesMap {
  [email: string]: {
    id: string;
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    notes: string | null;
    marketingConsent?: boolean;
    smsConsent?: boolean;
    marketingConsentAt?: string | null;
    smsConsentAt?: string | null;
    // Nouveaux champs enrichis
    civility?: string | null;
    country?: string | null;
    guestStatus?: string | null;
    reviewEmailConsent?: boolean;
    reviewEmailConsentAt?: string | null;
    reviewSmsConsent?: boolean;
    reviewSmsConsentAt?: string | null;
    company?: string | null;
    professionalEmail?: string | null;
    professionalPhone?: string | null;
    birthdate?: string | null;
    address?: string | null;
    city?: string | null;
    zip?: string | null;
    lang?: string | null;
    hasNoShow?: boolean;
    isBlacklisted?: boolean;
    allergiesNotes?: string | null;
    allergiesTags?: string[];
    guestTags?: string[];
    createdAt?: string | null;
    importedTotalReservations?: number;
    importedArrivedReservations?: number;
    importedTotalPersons?: number;
  };
}

export function aggregateClientData(
  reservations: Reservation[],
  customerNotesMap?: CustomerNotesMap
): CustomerData[] {
  const clientsMap = new Map<string, CustomerData>();

  // Build reverse index: customerId -> key (email lowercase or __no_email__<id>)
  const customerIdToKey = new Map<string, string>();
  if (customerNotesMap) {
    Object.entries(customerNotesMap).forEach(([key, data]) => {
      if (data.id) customerIdToKey.set(data.id, key);
    });
  }

  reservations.forEach((reservation) => {
    // Determine the client key: prefer email, fall back to customerId via reverse index
    let key: string | null = null;
    if (reservation.customerEmail && reservation.customerEmail.trim() !== '') {
      key = reservation.customerEmail.toLowerCase();
    } else if (reservation.customerId && customerIdToKey.has(reservation.customerId)) {
      key = customerIdToKey.get(reservation.customerId)!;
    }
    if (!key) return;

    if (!clientsMap.has(key)) {
      // Look up notes from the map if provided
      const customerData = customerNotesMap?.[key];
      
      const isNoEmailKey = key.startsWith('__no_email__');
      clientsMap.set(key, {
        id: customerData?.id || reservation.customerId,
        firstName: customerData?.firstName || reservation.customerFirstName,
        lastName: customerData?.lastName || reservation.customerLastName,
        email: isNoEmailKey ? '' : (reservation.customerEmail || ''),
        phone: customerData?.phone || reservation.customerPhone,
        notes: customerData?.notes || undefined,
        marketingConsent: customerData?.marketingConsent ?? false,
        smsConsent: customerData?.smsConsent ?? false,
        marketingConsentAt: customerData?.marketingConsentAt || undefined,
        smsConsentAt: customerData?.smsConsentAt || undefined,
        // Nouveaux champs enrichis
        civility: customerData?.civility || undefined,
        country: customerData?.country || undefined,
        guestStatus: customerData?.guestStatus || undefined,
        reviewEmailConsent: customerData?.reviewEmailConsent ?? false,
        reviewEmailConsentAt: customerData?.reviewEmailConsentAt || undefined,
        reviewSmsConsent: customerData?.reviewSmsConsent ?? false,
        reviewSmsConsentAt: customerData?.reviewSmsConsentAt || undefined,
        company: customerData?.company || undefined,
        professionalEmail: customerData?.professionalEmail || undefined,
        professionalPhone: customerData?.professionalPhone || undefined,
        birthdate: customerData?.birthdate || undefined,
        address: customerData?.address || undefined,
        city: customerData?.city || undefined,
        zip: customerData?.zip || undefined,
        lang: customerData?.lang || undefined,
        hasNoShow: customerData?.hasNoShow ?? false,
        isBlacklisted: customerData?.isBlacklisted ?? false,
        allergiesNotes: customerData?.allergiesNotes || undefined,
        allergiesTags: customerData?.allergiesTags || [],
        guestTags: customerData?.guestTags || [],
        arrivedReservations: 0,
        totalReservations: 0,
        totalPersons: 0,
        lastReservationDate: reservation.date,
        firstReservationDate: reservation.date,
        allReservations: [],
      });
    }

    const client = clientsMap.get(key)!;
    
    // Update customer ID if we find one (in case first reservation didn't have it)
    if (!client.id && reservation.customerId) {
      client.id = reservation.customerId;
    }
    
    // Update notes from customer data if available and not already set
    if (!client.notes && customerNotesMap?.[key]?.notes) {
      client.notes = customerNotesMap[key].notes || undefined;
    }
    
    client.totalReservations++;
    if (reservation.status === 'arrivé') {
      client.arrivedReservations++;
    }
    client.totalPersons += reservation.numberOfPeople + (reservation.numberOfChildren || 0);
    // Mark as having real reservations so we don't double-add imported counts
    (client as any)._hasRealReservations = true;
    client.allReservations.push(reservation);

    if (reservation.date > client.lastReservationDate) {
      client.lastReservationDate = reservation.date;
    }
    if (reservation.date < client.firstReservationDate) {
      client.firstReservationDate = reservation.date;
    }
  });

  // Phase 3: Inject orphan customers (exist in DB but have no reservations)
  if (customerNotesMap) {
    Object.entries(customerNotesMap).forEach(([key, customerData]) => {
      // Skip shared anonymous walk-in customers from CRM
      const isNoEmailClient = key.startsWith('__no_email__');
      if (
        customerData.firstName === 'Walk-in' &&
        (!customerData.lastName || customerData.lastName.trim() === '') &&
        isNoEmailClient
      ) {
        return;
      }

      const lookupKey = key;

      if (!clientsMap.has(lookupKey)) {
        clientsMap.set(lookupKey, {
          id: customerData.id,
          firstName: customerData.firstName || '',
          lastName: customerData.lastName || '',
          email: isNoEmailClient ? '' : key,
          phone: customerData.phone || undefined,
          notes: customerData.notes || undefined,
          marketingConsent: customerData.marketingConsent ?? false,
          smsConsent: customerData.smsConsent ?? false,
          marketingConsentAt: customerData.marketingConsentAt || undefined,
          smsConsentAt: customerData.smsConsentAt || undefined,
          civility: customerData.civility || undefined,
          country: customerData.country || undefined,
          guestStatus: customerData.guestStatus || undefined,
          reviewEmailConsent: customerData.reviewEmailConsent ?? false,
          reviewEmailConsentAt: customerData.reviewEmailConsentAt || undefined,
          reviewSmsConsent: customerData.reviewSmsConsent ?? false,
          reviewSmsConsentAt: customerData.reviewSmsConsentAt || undefined,
          company: customerData.company || undefined,
          professionalEmail: customerData.professionalEmail || undefined,
          professionalPhone: customerData.professionalPhone || undefined,
          birthdate: customerData.birthdate || undefined,
          address: customerData.address || undefined,
          city: customerData.city || undefined,
          zip: customerData.zip || undefined,
          lang: customerData.lang || undefined,
          hasNoShow: customerData.hasNoShow ?? false,
          isBlacklisted: customerData.isBlacklisted ?? false,
          allergiesNotes: customerData.allergiesNotes || undefined,
          allergiesTags: customerData.allergiesTags || [],
          guestTags: customerData.guestTags || [],
          arrivedReservations: customerData.importedArrivedReservations || 0,
          totalReservations: customerData.importedTotalReservations || 0,
          totalPersons: customerData.importedTotalPersons || 0,
          lastReservationDate: customerData.createdAt ? new Date(customerData.createdAt) : new Date(0),
          firstReservationDate: customerData.createdAt ? new Date(customerData.createdAt) : new Date(0),
          allReservations: [],
        });
      }
    });

    // Phase 4: Add imported counts to clients that also have real reservations
    clientsMap.forEach((client, key) => {
      if ((client as any)._hasRealReservations && customerNotesMap[key]) {
        const imported = customerNotesMap[key];
        client.totalReservations += imported.importedTotalReservations || 0;
        client.arrivedReservations += imported.importedArrivedReservations || 0;
        client.totalPersons += imported.importedTotalPersons || 0;
      }
      delete (client as any)._hasRealReservations;
    });
  }

  return Array.from(clientsMap.values());
}

export function calculateClientStats(clients: CustomerData[]): ClientStats {
  const startOfCurrentMonth = startOfMonth(new Date());

  const newClientsThisMonth = clients.filter((client) => {
    try {
      return isAfter(client.firstReservationDate, startOfCurrentMonth);
    } catch {
      return false;
    }
  }).length;

  return {
    totalClients: clients.length,
    newClientsThisMonth,
  };
}

/**
 * Normalise une chaîne pour la recherche : minuscules, suppression des accents, trim.
 */
export function normalizeForSearch(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * Normalise un numéro de téléphone pour la recherche en supprimant
 * l'indicatif international suisse (+41, 0041, 41) et le 0 de tête.
 */
export function normalizePhoneForSearch(phone: string): string {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('41') && digits.length > 9) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

export function filterClients(
  clients: CustomerData[],
  searchTerm: string
): CustomerData[] {
  let filtered = clients;

  // Filtre par recherche
  if (searchTerm) {
    const term = normalizeForSearch(searchTerm);
    const phoneTerm = normalizePhoneForSearch(searchTerm);
    filtered = filtered.filter(
      (client) =>
        (client.firstName && normalizeForSearch(client.firstName).includes(term)) ||
        (client.lastName && normalizeForSearch(client.lastName).includes(term)) ||
        (client.email && normalizeForSearch(client.email).includes(term)) ||
        (phoneTerm.length > 0 && client.phone && normalizePhoneForSearch(client.phone).includes(phoneTerm)) ||
        (client.company && normalizeForSearch(client.company).includes(term)) ||
        (client.city && normalizeForSearch(client.city).includes(term)) ||
        (client.allergiesNotes && normalizeForSearch(client.allergiesNotes).includes(term)) ||
        (client.guestTags && client.guestTags.some(tag => normalizeForSearch(tag).includes(term))) ||
        (client.allergiesTags && client.allergiesTags.some(tag => normalizeForSearch(tag).includes(term)))
    );
  }

  return filtered;
}

export function sortClients(
  clients: CustomerData[],
  sortBy: "firstName" | "lastName" | "reservations" | "lastVisit"
): CustomerData[] {
  const sorted = [...clients];

  switch (sortBy) {
    case "lastName":
      sorted.sort((a, b) => {
        const cmp = (a.lastName || "").localeCompare(b.lastName || "");
        return cmp !== 0 ? cmp : (a.firstName || "").localeCompare(b.firstName || "");
      });
      break;
    case "firstName":
      sorted.sort((a, b) => {
        const cmp = (a.firstName || "").localeCompare(b.firstName || "");
        return cmp !== 0 ? cmp : (a.lastName || "").localeCompare(b.lastName || "");
      });
      break;
    case "reservations":
      sorted.sort((a, b) => b.totalReservations - a.totalReservations);
      break;
    case "lastVisit":
      sorted.sort((a, b) => {
        const bTime = b.lastReservationDate?.getTime() || 0;
        const aTime = a.lastReservationDate?.getTime() || 0;
        return bTime - aTime;
      });
      break;
  }

  return sorted;
}

/**
 * Formate un nom client en gérant les champs vides/null
 */
export function formatCustomerName(firstName?: string | null, lastName?: string | null, fallback = 'Client'): string {
  return [firstName, lastName].filter(Boolean).join(' ') || fallback;
}

export type LoyaltyLevel = 'none' | 'returning' | 'loyal' | 'vip';

export function getClientLoyaltyLevel(
  email: string | undefined,
  clients: CustomerData[]
): LoyaltyLevel {
  if (!email) return 'none';
  
  const client = clients.find(c => c.email.toLowerCase() === email.toLowerCase());
  if (!client) return 'none';
  
  if (client.arrivedReservations >= 10) return 'vip';
  if (client.arrivedReservations >= 5) return 'loyal';
  if (client.arrivedReservations >= 2) return 'returning';
  return 'none';
}
